import { trpc } from '@org/clients/school';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
  SortableContentList,
  Table,
  cn,
  unwrapTrpcData,
} from '@org/ui';
import type { JourneySelect } from '@org/model';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { createColumnHelper } from '@tanstack/react-table';
import { InfoIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { TRPCClientError } from '@trpc/client';

export type JourneysDialogEntityType = 'user' | 'group';

type JourneysDialogProps = {
  entityType: JourneysDialogEntityType;
  entityId: string;
  entityLabel?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type JourneyAccessSource = 'teacher' | 'path' | 'purchase' | 'admin';

type SelectedJourney = {
  journeyId: string;
  name: string;
  description?: string | null;
  order: number;
  active?: boolean;
  source?: JourneyAccessSource | null;
};

// Rows granted by a learning path or a purchase are managed by the system,
// not by hand: they stay in the list (the save only upserts teacher/admin
// rows) so the dialog shows them read-only and blocks removal.
const isLockedSource = (source?: JourneyAccessSource | null) =>
  source === 'path' || source === 'purchase';

export function JourneysDialog({
  entityType,
  entityId,
  entityLabel,
  open,
  onOpenChange,
}: JourneysDialogProps) {
  const { t } = useTranslation('admin');
  const utils = trpc.useUtils();
  const [selectedJourneys, setSelectedJourneys] = useState<SelectedJourney[]>([]);
  const [activeJourneyId, setActiveJourneyId] = useState<string | undefined>(undefined);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // All journeys (for the availability table)
  const { data: allJourneysData } = trpc.journeys.getJourneys.useQuery(undefined, {
    enabled: open,
  });
  const allJourneys = useMemo(
    () => unwrapTrpcData<JourneySelect[]>(allJourneysData as JourneySelect[]) ?? [],
    [allJourneysData],
  );

  // User's assigned journeys
  const { data: userJourneysData, isLoading: isLoadingUserJourneys } =
    trpc.journeys.getUserJourneys.useQuery(
      { userId: entityId, activeOnly: false },
      { enabled: open && entityType === 'user' && !!entityId },
    );

  // Group's assigned journeys
  const { data: groupJourneysData, isLoading: isLoadingGroupJourneys } =
    trpc.journeys.getGroupJourneys.useQuery(
      { groupId: entityId, activeOnly: false },
      { enabled: open && entityType === 'group' && !!entityId },
    );

  // Active journey (user-only — groups don't have per-user active tracking per D-2)
  const { data: activeJourneyData } = trpc.progress.getActiveJourney.useQuery(
    { userId: entityId },
    { enabled: open && entityType === 'user' && !!entityId },
  );
  const activeJourney = useMemo(
    () =>
      unwrapTrpcData<{ journeyId: string; unitId: string } | null>(activeJourneyData as any) ??
      null,
    [activeJourneyData],
  );

  const assignedData = entityType === 'user' ? userJourneysData : groupJourneysData;
  const isLoadingAssigned = entityType === 'user' ? isLoadingUserJourneys : isLoadingGroupJourneys;

  // Seed selectedJourneys from the assigned list when dialog opens
  useEffect(() => {
    if (!open) return;
    const unwrapped = unwrapTrpcData<
      Array<{ journeys: JourneySelect; active?: boolean; source?: JourneyAccessSource | null }>
    >(assignedData as any);
    if (unwrapped && unwrapped.length > 0) {
      const mapped: SelectedJourney[] = unwrapped.map((item, index) => ({
        journeyId: item.journeys.journeyId,
        name: item.journeys.name ?? t('journeys.dialog.unnamedJourney', 'Unnamed Journey'),
        description: item.journeys.description,
        order: index,
        active: item.active,
        source: item.source,
      }));
      setSelectedJourneys(mapped);
      // Pre-select the active journey radio from the fetched data
      const activeItem = unwrapped.find((item) => item.active);
      setActiveJourneyId(activeItem?.journeys.journeyId);
    } else {
      setSelectedJourneys([]);
      setActiveJourneyId(undefined);
    }
  }, [assignedData, open, t]);

  const saveUserJourneysMutation = trpc.journeys.saveJourneysForUser.useMutation({
    onSuccess: async () => {
      toast.success(t('journeys.dialog.saveSuccess'));
      await utils.journeys.getUserJourneys.invalidate({ userId: entityId, activeOnly: false });
      await utils.progress.getActiveJourney.invalidate({ userId: entityId });
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof TRPCClientError ? error.message : 'An unexpected error occurred';
      toast.error(t('journeys.dialog.saveError', { message }));
    },
  });

  const saveGroupJourneysMutation = trpc.journeys.saveJourneysForGroup.useMutation({
    onSuccess: async () => {
      toast.success(t('journeys.dialog.saveSuccess'));
      await utils.journeys.getGroupJourneys.invalidate({ groupId: entityId, activeOnly: false });
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof TRPCClientError ? error.message : 'An unexpected error occurred';
      toast.error(t('journeys.dialog.saveError', { message }));
    },
  });

  const isSaving = saveUserJourneysMutation.isPending || saveGroupJourneysMutation.isPending;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;
    setSelectedJourneys((items) => {
      const oldIndex = items.findIndex((item) => item.journeyId === active.id);
      const newIndex = items.findIndex((item) => item.journeyId === over.id);
      return arrayMove(items, oldIndex, newIndex).map((item, index) => ({
        ...item,
        order: index,
      }));
    });
  };

  const toggleJourneySelection = (journey: JourneySelect) => {
    setSelectedJourneys((prev) => {
      const existing = prev.find((j) => j.journeyId === journey.journeyId);
      if (existing && isLockedSource(existing.source)) {
        return prev;
      }
      const isSelected = !!existing;
      if (isSelected) {
        return prev
          .filter((j) => j.journeyId !== journey.journeyId)
          .map((j, idx) => ({ ...j, order: idx }));
      }
      return [
        ...prev,
        {
          journeyId: journey.journeyId,
          name: journey.name ?? t('journeys.dialog.unnamedJourney', 'Unnamed Journey'),
          description: journey.description,
          order: prev.length,
        },
      ];
    });
  };

  const handleJourneyRemoval = (item: SelectedJourney) => {
    if (isLockedSource(item.source)) return;
    setSelectedJourneys((prev) =>
      prev
        .filter((j) => j.journeyId !== item.journeyId)
        .map((j, idx) => ({ ...j, order: idx })),
    );
    if (activeJourneyId === item.journeyId) {
      setActiveJourneyId(undefined);
    }
  };

  const handleSave = () => {
    const payload = selectedJourneys.map((j, idx) => ({
      journeyId: j.journeyId,
      order: j.order ?? idx,
    }));

    if (entityType === 'user') {
      saveUserJourneysMutation.mutate({ userId: entityId, journeys: payload, activeJourneyId });
    } else {
      saveGroupJourneysMutation.mutate({ groupId: entityId, journeys: payload, activeJourneyId });
    }
  };

  const columnHelper = createColumnHelper<JourneySelect>();
  const journeyColumns = [
    columnHelper.accessor(
      (row) => row.name || t('journeys.dialog.unnamedJourney', 'Unnamed Journey'),
      {
        id: 'name',
        header: t('journeys.dialog.columnName'),
      },
    ),
    columnHelper.accessor((row) => row.description || '', {
      id: 'description',
      header: t('journeys.dialog.columnDescription'),
    }),
    columnHelper.display({
      id: 'select',
      header: t('journeys.dialog.columnSelect'),
      cell: (info) => {
        const row = info.row.original;
        const selected = selectedJourneys.find((j) => j.journeyId === row.journeyId);
        const isSelected = !!selected;
        const isLocked = isLockedSource(selected?.source);
        return (
          <Button
            variant="outline"
            size="sm"
            aria-pressed={isSelected}
            disabled={isLocked}
            className={cn(isSelected && 'ring-2 ring-primary')}
            onClick={() => toggleJourneySelection(row)}
          >
            {isSelected ? t('journeys.dialog.selected') : t('journeys.dialog.select')}
          </Button>
        );
      },
    }),
  ];

  const renderSelectedItem = (item: SelectedJourney, index: number) => (
    <div className="flex items-center gap-3 w-full">
      <Badge
        variant="outline"
        className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full font-medium text-sm p-0"
      >
        {index + 1}
      </Badge>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-foreground truncate">{item.name}</h4>
        {item.description && (
          <p className="text-sm text-muted-foreground mt-0.5 truncate">{item.description}</p>
        )}
      </div>
    </div>
  );

  const renderItemActions = (
    item: SelectedJourney,
    onRemove: (item: SelectedJourney) => void,
  ) => {
    const isActive = activeJourneyId === item.journeyId;
    const isLocked = isLockedSource(item.source);
    return (
      <div className="flex items-center gap-2">
        {isLocked && (
          <span className="text-xs italic text-muted-foreground" data-no-dnd="true">
            {item.source === 'purchase'
              ? t('journeys.dialog.sourcePurchase')
              : t('journeys.dialog.sourcePath')}
          </span>
        )}
        <Button
          type="button"
          variant={isActive ? 'default' : 'outline'}
          size="sm"
          aria-pressed={isActive}
          className={cn(
            isActive &&
              'bg-emerald-600 hover:bg-emerald-600 text-white border-emerald-600',
          )}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setActiveJourneyId((prev) =>
              prev === item.journeyId ? undefined : item.journeyId,
            );
          }}
          data-no-dnd="true"
        >
          {isActive
            ? t('journeys.dialog.activeBadge')
            : t('journeys.dialog.makeActive')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={isLocked}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onRemove(item);
          }}
          className="text-muted-foreground hover:text-red-600"
          data-no-dnd="true"
        >
          {t('journeys.dialog.remove')}
        </Button>
      </div>
    );
  };

  const renderSelectedJourneysList = () => {
    if (selectedJourneys.length === 0) {
      return (
        <Alert variant="info" className="my-4">
          <InfoIcon className="h-4 w-4" />
          <AlertTitle>{t('journeys.dialog.noAssigned')}</AlertTitle>
          <AlertDescription>{t('journeys.dialog.noAssignedDescription')}</AlertDescription>
        </Alert>
      );
    }

    return (
      <Card className="mb-6 shadow-sm">
        <CardHeader className="bg-muted/50 rounded-t-md border-b pb-3 pt-3">
          <div className="flex justify-between items-center">
            <CardTitle className="text-base font-medium text-foreground">
              {t('journeys.dialog.assignedTitle', { count: selectedJourneys.length })}
            </CardTitle>
            <Badge variant="outline">{t('journeys.dialog.dragToReorder')}</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={selectedJourneys.map((j) => j.journeyId)}
              strategy={verticalListSortingStrategy}
            >
              <SortableContentList
                items={selectedJourneys}
                onRemove={handleJourneyRemoval}
                idKey="journeyId"
                nameKey="name"
                descriptionKey="description"
                renderItem={renderSelectedItem}
                renderItemActions={renderItemActions}
              />
            </SortableContext>
          </DndContext>
        </CardContent>
      </Card>
    );
  };

  const titleKey = entityType === 'user' ? 'journeys.dialog.titleUser' : 'journeys.dialog.titleGroup';
  const title = t(titleKey, { name: entityLabel ?? '' });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {entityType === 'user'
              ? t('journeys.dialog.descriptionUser', 'Manage this student\'s journey assignments and their order.')
              : t('journeys.dialog.descriptionGroup', 'Manage this group\'s journey assignments and their order.')}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {isLoadingAssigned ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            <>
              {renderSelectedJourneysList()}
              <div className="mt-8">
                <h3 className="font-medium mb-3 text-foreground border-b pb-2">
                  {t('journeys.dialog.availableTitle')}
                </h3>
                <Table
                  tableData={allJourneys}
                  columns={journeyColumns}
                  getRowId={(row: any) => row.journeyId}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            {t('journeys.dialog.cancel')}
          </Button>
          <Button variant="outline" onClick={handleSave} isLoading={isSaving} loadingText={t('common.status.saving')}>
            {t('journeys.dialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
